import React from 'react';
import { Link } from 'react-router-dom';

import { TRY_FREE_PATH } from './contact'
import { ENTRY_TIER, LARGEST_TIER, formatPrice } from './pricingTiers'
import {
	AI_REMOVAL_EXACT,
	AI_REMOVAL_OTHERS,
	EXACT_RECOVERY_AVERAGE,
	PAYLOAD_BITS,
	PSNR_DB,
	SSIM_PERCENT,
} from './robustness'
import { P95_WINS, RATE_WINS, SCENARIO_COUNT, SPEEDUP_MAX, SPEEDUP_MEDIAN, WATERMARK_COST_MAX } from './metrics'

// Every answer draws on a figure declared elsewhere on the site (metrics.ts,
// robustness.ts, pricingTiers.ts) or on a claim the home page already makes,
// so the FAQ cannot drift from the pages it summarizes.

export interface FaqEntry {
	question: string
	answer: React.ReactNode
}

export interface FaqGroup {
	title: string
	entries: FaqEntry[]
}

export const FAQ_GROUPS: FaqGroup[] = [
	{
		title: 'Product',
		entries: [
			{
				question: 'What is Underlayer?',
				answer: (
					<>
						A self-hosted API for image processing that invisibly signs everything it serves.
						It sits in your pipeline like an image proxy, resizing, blurring, re-encoding and
						signing URLs, and embeds an invisible identifier in every image on the way out. A
						second endpoint reads that identifier back from any copy that comes back to you.
					</>
				),
			},
			{
				question: 'Who is it for?',
				answer: (
					<>
						Content platforms that want more traceability over the images they distribute:
						media, marketplaces, publishers, rights holders. Every copy served can carry its
						own identifier, so a copy found elsewhere can be traced back to where it came
						from: a subscriber, a partner, a channel.
					</>
				),
			},
			{
				question: 'Is it available today?',
				answer: (
					<>
						Underlayer is currently in testing, and you can test it for free on your own
						infrastructure: <Link to={ TRY_FREE_PATH } >request a trial</Link> and we set it
						up with you.
					</>
				),
			},
			{
				question: 'Does it handle video?',
				answer: (
					<>
						Not yet. Today the API processes images; video is next on the roadmap.
					</>
				),
			},
		],
	},
	{
		title: 'The watermark',
		entries: [
			{
				question: 'Can the watermark be seen?',
				answer: (
					<>
						No. Watermarked images measure { PSNR_DB } dB PSNR and { SSIM_PERCENT }% SSIM against
						the original on 1000 test images, the second most invisible result among the seven
						methods we benchmarked. Compare an original and a watermarked image yourself on
						the <Link to="/technology" >Technology page</Link>.
					</>
				),
			},
			{
				question: 'What does it survive?',
				answer: (
					<>
						The transformations an image meets once it is shared: JPEG, WebP, AVIF and HEIC
						recompression, cropping, rotation, resizing, mirroring, noise, filters, neural
						compression codecs and AI-based watermark removal. Across those fourteen attacks the
						full identifier comes back { EXACT_RECOVERY_AVERAGE }% of the time on average, and
						more than 90% of the time on every single one.
					</>
				),
			},
			{
				question: 'Does it survive AI watermark removers?',
				answer: (
					<>
						Yes. Those removers are built for the visible text watermarks stamped on stock
						photos: an AI detector locates the mark, an inpainting model regenerates the
						area. Put through that chain, { AI_REMOVAL_EXACT }% of identifiers still decode
						exactly. Neural methods from the literature fall to { AI_REMOVAL_OTHERS } on the
						same test.
					</>
				),
			},
			{
				question: 'What about screenshots?',
				answer: (
					<>
						A screenshot is a crop, a resize and a recompression. Each of those is in the
						benchmark, and each returns the full identifier in 100% of cases at the tested
						settings. Cropping, for instance, still decodes above 90% with a fifth of the image
						removed on every edge.
					</>
				),
			},
			{
				question: 'How much information does it carry?',
				answer: (
					<>
						{ PAYLOAD_BITS } bits of payload per image, protected by an error-correcting code.
						That is enough to give every download its own identifier: a subscriber, an order,
						a session.
					</>
				),
			},
			{
				question: 'Do I need the original image to detect the watermark?',
				answer: (
					<>
						No. Detection reads the identifier from the pixels of the copy you found, with no
						original image and no metadata.
					</>
				),
			},
			{
				question: 'Why not just use metadata, like EXIF or C2PA?',
				answer: (
					<>
						Because metadata is stripped the moment an image is screenshotted or re-uploaded.
						Underlayer carries the identifier in the pixels, where it survives. For
						AI-generated content it also writes a signed C2PA manifest, since the AI Act's Code
						of Practice asks for both layers.
					</>
				),
			},
		],
	},
	{
		title: 'Deployment',
		entries: [
			{
				question: 'Where does it run?',
				answer: (
					<>
						On your own servers, as one Docker container in your existing image pipeline.
						Nothing leaves your environment: no third-party service in the data path, no
						per-image API fees.
					</>
				),
			},
			{
				question: 'What hardware does it need?',
				answer: (
					<>
						A Docker host with an NVIDIA GPU: the watermark model runs on CUDA, and the image
						pipeline is written in C++. Our benchmark machine is an 8-core Intel Core i7 with an
						RTX 3090.
					</>
				),
			},
			{
				question: 'How fast is it?',
				answer: (
					<>
						On that machine, faster than imgproxy, the fastest open-source image proxy, on
						{ ' ' }{ RATE_WINS } of the { SCENARIO_COUNT } scenarios measured: five formats, passed through,
						resized or blurred. A median of { SPEEDUP_MEDIAN } and up to { SPEEDUP_MAX } more images per
						second, and a lower 95th-percentile latency on { P95_WINS } of them. The invisible
						watermark, computed on the GPU, costs from nothing measurable on AVIF to
						{ ' ' }{ WATERMARK_COST_MAX } of throughput on JPEG, the fastest pipeline. The full numbers
						are on the <Link to="/performances" >Performances page</Link>.
					</>
				),
			},
			{
				question: 'Which formats are supported?',
				answer: (
					<>
						JPEG, WebP, AVIF, PNG, BMP and HEIC, with HDR supported end to end, native HTTPS
						and URL signing. GIF, TIFF, SVG, JPEG XL, PDF, PSD and camera RAW are not supported
						yet, nor is smart cropping. Reading sources directly from Amazon S3 and similar
						storage is on the roadmap.
					</>
				),
			},
			{
				question: 'How long does integration take?',
				answer: (
					<>
						An afternoon, not a six-month project. Start the container, call the embed endpoint
						on the way out, and call detect on anything that comes back.
					</>
				),
			},
		],
	},
	{
		title: 'Pricing',
		entries: [
			{
				question: 'How is Underlayer priced?',
				answer: (
					<>
						By the number of unique source assets protected during the contract year, from
						{ ' ' }{ ENTRY_TIER.assets.toLocaleString( 'en-US' ) } assets at { formatPrice( ENTRY_TIER.price ) } per
						year to { LARGEST_TIER.assets.toLocaleString( 'en-US' ) } at { formatPrice( LARGEST_TIER.price ) }.
						Larger libraries are quoted. Watermark operations are unlimited: embed and decode
						as much as your business requires. Details on the <Link to="/pricing" >Pricing page</Link>.
					</>
				),
			},
			{
				question: 'What counts as an asset?',
				answer: (
					<>
						A unique source image. Re-encoding that same asset for different users, channels,
						sessions or transactions does not consume additional capacity, however many
						individualized copies you generate.
					</>
				),
			},
		],
	},
	{
		title: 'Company',
		entries: [
			{
				question: 'Who builds Underlayer?',
				answer: (
					<>
						Two engineers who spent years in image processing and machine learning: Clement
						Gerber, senior software engineer in image processing and high-performance C++, and
						Justin Reverdi, research engineer with a PhD in machine learning robustness.
					</>
				),
			},
		],
	},
]
